import React, { useState } from 'react'

import { BadgeCheck, BadgeXIcon, X } from 'lucide-react'
import { convertToCurrencyRounded, getDiscount } from '@/utils/currency_converter'
import { FinalSalesData } from '../-data/models/inventoryData'
import { Button } from '@/components/ui/button'
import {
    Sheet,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useNavigate } from '@tanstack/react-router'
import { Input } from '@/components/ui/input'
import AttachPatient from './attachPatient'
import { PatientMinimal } from '@/routes/(pharma)/_layout/patients/-data/schema'
import { useLiveQuery } from 'dexie-react-hooks'
import db, { SalesItems, SalesItemsBatches } from '@/db'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CreateSaleRequest } from '@/gen/sales/create_sale_pb'
import { PaymentInfo, PaymentMode, SaleItem, TaxInfo } from '@/gen/sales/common_pb'
import { TaxProfile } from '@/gen/medical_master/common_pb'
import { ItemType } from '@/gen/inventory/common_pb'
import { pharmacyClient } from '@/connect'
import { toast } from '@/components/ui/use-toast'
import QRCode from 'react-qr-code'

const Component1 = ({ head, foot, breakup = [] }: any) => {
    return (
        <Tooltip delayDuration={0}>
            <TooltipTrigger className='cursor-pointer'>
                <span className='flex items-center mx-2'>
                    <p className='text-sm font-semibold mr-8'>{head}</p>
                    <p className='text-gray-400 text-sm'>{foot}</p>
                </span>
            </TooltipTrigger>
            {breakup.length > 0 && <TooltipContent>
                <div className='flex flex-col'>
                    <p className='text-sm font-semibold mb-2'>{head} Breakup</p>
                    {breakup.map((item: any, index: number) => (
                        <div key={index} className='flex justify-between text-sm mb-1 gap-8'>
                            <span>{item.name}</span>
                            <span>{convertToCurrencyRounded(item.amount)}</span>
                        </div>
                    ))}
                </div>
            </TooltipContent>}
        </Tooltip>
    )
}

const formData = (currentSaleItems: SalesItems[] | undefined, currentSaleItemsBatchesSelected: SalesItemsBatches[] | undefined): FinalSalesData[] => {
    if (currentSaleItems && currentSaleItemsBatchesSelected) {
        const finalData: FinalSalesData[] = currentSaleItems.map((item) => {
            const batchInfo = currentSaleItemsBatchesSelected.filter((batch) => batch.item_id === item.item_id && batch.type === item.type)
            return {
                id: item.item_id,
                trade_name: item.trade_name,
                barcode: item.barcode!,
                unit: item.unit,
                unit_type: item.unit_type,
                type: item.type === "MEDICAL" ? "MEDICAL" : "NON_MEDICAL",
                inventory_id: batchInfo[0].inventory_id,
                batch_number: batchInfo[0].batch_number,
                expiry_date: batchInfo[0].expiry_date!,
                cost: batchInfo[0].cost,
                price: batchInfo[0].price,
                available_stock: batchInfo[0].available_stock,
                sold_quantity: batchInfo[0].sold_quantity,
                quantity_added: item.quantity_added,
                item_discount: item.item_discount,
                tax_profile: TaxProfile.fromJsonString(batchInfo[0].tax_profile),
                discount_type: batchInfo[0].max_discount_type || "",
            }
        })
        return finalData
    } else {
        throw new Error("No items selected")
    }
}

const Footer = ({
    total,
    discount = 0,
    totalTax,
    internalSalesId,
    applicableTaxes = [],
    checkoutButtonRef
}: {
    total: number,
    discount: number,
    totalTax: number,
    applicableTaxes?: { name: string | undefined; rate: number | undefined; amount: number; }[],
    internalSalesId: number,
    checkoutButtonRef: React.RefObject<HTMLButtonElement>
}) => {

    const pharmacyMetadata = JSON.parse(localStorage.getItem("pharmacyMetadata") || "{}")
    if (!pharmacyMetadata || !pharmacyMetadata.countryCode) {
        console.error("Pharmacy metadata is not set or does not contain country code.")
        return null; // or handle the error as needed
    }

    const [openCheckout, setOpenCheckout] = React.useState<boolean>(false)
    const [tabState, setTabState] = React.useState<string>('cash')

    //Common
    const [addDiscount, setAddDiscount] = useState<number>(discount)
    const [loading, setLoading] = useState<boolean>(false)
    const navigate = useNavigate()

    //Cash
    const [cashReceived, setCashReceived] = useState<number | null>(null)

    //Card
    const [approvalCode, setApprovalCode] = useState<string>("")
    const [last4digits, setLast4digits] = useState<string>("")

    //Split
    const [cardAmount, setCardAmount] = useState<number | null>(null)
    const [upiAmount, setUpiAmount] = useState<number | null>(null)

    //Patient
    const [patientInfo, setPatientInfo] = useState<PatientMinimal>()

    React.useEffect(() => {
        setAddDiscount(discount);
    }, [discount]);


    // const currentSaleItems = useLiveQuery<SaleItemSQLData>(`SELECT * FROM sales_items WHERE sale_id = $1`, [internalSalesId])
    // const currentSaleItemsBatchesSelected = useLiveQuery<SaleItemBatchSQLData>(`SELECT * FROM sales_items_batches WHERE sale_id = $1 AND is_selected = TRUE`, [internalSalesId]);

    const currentSaleItems = useLiveQuery(() => db.sales_items.where('sale_id').equals(internalSalesId).toArray())
    const currentSaleItemsBatchesSelected = useLiveQuery(() => db.sales_items_batches.where('sale_id').equals(internalSalesId).and((item) => item.is_selected === true).toArray())

    return (
        <div className='flex justify-between items-center bg-[#F6F6F6] sticky bottom-0 rounded-2xl h-[50px]'>
            <div className='flex'>
                <Component1
                    head='Co-Pay'
                    foot={convertToCurrencyRounded(0)}
                />
                <Component1
                    head='Disc.'
                    foot={convertToCurrencyRounded(addDiscount)}
                />
                <Component1
                    head='Net Amt.'
                    foot={convertToCurrencyRounded(total - totalTax)}
                />
                <Component1
                    head='Tax'
                    foot={convertToCurrencyRounded(totalTax)}
                    breakup={applicableTaxes}
                />
            </div>

            <div className='bg-[#F6F6F6] flex space-x-2'>
                <Component1
                    head='Total Amount'
                    foot={convertToCurrencyRounded(total)}
                />
                <Sheet
                    open={openCheckout}
                >
                    <SheetTrigger asChild>
                        <Button
                            className='bg-blue-700 px-6 rounded-none rounded-r-2xl'
                            size={'lg'}
                            onClick={() => setOpenCheckout(true)}
                            ref={checkoutButtonRef}
                            disabled={!currentSaleItems?.length}
                        >
                            <BadgeCheck className='mr-2' />
                            Checkout
                        </Button>
                    </SheetTrigger>
                    <SheetContent
                        side={'bottom'}
                        className=''
                    >
                        <SheetHeader className='border-b-[0.5px] pb-4'>
                            <SheetTitle>Checkout</SheetTitle>
                            <Button
                                variant={'ghost'}
                                className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
                                onClick={() => setOpenCheckout(false)}
                            >
                                <X className="h-4 w-4" />
                                <span className="sr-only">Close</span>
                            </Button>
                        </SheetHeader>
                        <div className='my-4'>
                            <Tabs
                                defaultValue={tabState}
                                onValueChange={(value) => setTabState(value)}
                                className='grid'
                            >
                                <TabsList className='flex w-fit justify-start al'>
                                    <TabsTrigger value="cash" className='w-[150px]'>Cash</TabsTrigger>
                                    <TabsTrigger value="card" className='w-[150px]'>Card</TabsTrigger>
                                    {pharmacyMetadata.countryCode === "IN" && <TabsTrigger value="upi" className='w-[150px]'>UPI</TabsTrigger>}
                                    <TabsTrigger value="split" className='w-[150px]'>Split</TabsTrigger>
                                </TabsList>
                                <hr className='h-[10px] w-screen my-4' />
                                <TabsContent value="cash" className=''>
                                    <div className='grid grid-cols-2 divide-x-2 w-screen'>
                                        <div className='mx-auto w-1/2'>
                                            <div className='flex flex-col items-center justify-center'>
                                                <p className='text-gray-600 font-semibold'>Total for {currentSaleItems?.length} item(s)</p>
                                                <div
                                                    className='border rounded-lg px-4 py-2 text-2xl font-bold'
                                                >{convertToCurrencyRounded(total)}</div>

                                                <div className='grid grid-cols-3 divide-x-2 place-content-center mt-4'>
                                                    <div className='flex flex-col items-center justify-center px-4'>
                                                        <p className='text-gray-600 text-sm'>Cash Received</p>
                                                        <p className='font-semibold'>{convertToCurrencyRounded(cashReceived!)}</p>
                                                    </div>
                                                    <div className='flex flex-col items-center justify-center'>
                                                        <p className='text-gray-600 text-sm'>{(cashReceived!) >= total ? "Change" : "Balance"}</p>
                                                        <p className='font-semibold'>{convertToCurrencyRounded(cashReceived! - total, false)}</p>
                                                    </div>
                                                    <div className='flex flex-col items-center justify-center'>
                                                        <p className='text-gray-600 text-sm'>Discount</p>
                                                        <p className='font-semibold'>{convertToCurrencyRounded(addDiscount!)}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className='border-t-[0.5px] mt-4'>
                                                <p className='text-sm font-semibold mt-4 mb-2'>Cash Received<span className='text-red-400'>*</span></p>
                                                <Input
                                                    placeholder='45.00'
                                                    value={cashReceived!}
                                                    type='number'
                                                    onChange={e => setCashReceived(e.target.value ? parseFloat(e.target.value) : null)}
                                                />
                                            </div>
                                        </div>

                                        <AttachPatient setPatientInfo={setPatientInfo} patientInfo={patientInfo}
                                        />
                                    </div>
                                </TabsContent>
                                <TabsContent value="card">
                                    <div className='grid grid-cols-2 divide-x-2 w-screen'>
                                        <div className='mx-auto w-1/2'>
                                            <div className='flex flex-col items-center justify-center'>
                                                <p className='text-gray-600 font-semibold'>Total for {currentSaleItems?.length} item(s)</p>
                                                <div
                                                    className='border rounded-lg px-4 py-2 text-2xl font-bold'
                                                >{convertToCurrencyRounded(total)}</div>
                                            </div>

                                            <div className='border-t-[0.5px] mt-4'>
                                                <p className='text-sm font-semibold mt-4 mb-2'>Approval Code<span className='text-red-400'>*</span></p>
                                                <Input
                                                    placeholder='16AC'
                                                    value={approvalCode}
                                                    onChange={e => setApprovalCode(e.target.value)}
                                                />

                                                <p className='text-sm font-semibold mt-4 mb-2'>Last 4 Digits<span className='text-red-400'>*</span></p>
                                                <Input
                                                    placeholder='5678'
                                                    value={last4digits}
                                                    type='number'
                                                    onChange={e => setLast4digits(e.target.value)}
                                                />


                                            </div>
                                        </div>

                                        <AttachPatient setPatientInfo={setPatientInfo} patientInfo={patientInfo}
                                        />

                                    </div>
                                </TabsContent>
                                {pharmacyMetadata.countryCode === "IN" && (
                                    <TabsContent value="upi">
                                        <div className='grid grid-cols-2 divide-x-2 w-screen'>
                                            <div className='mx-auto w-1/2'>
                                                <div className='flex flex-col items-center justify-center'>
                                                    <p className='text-gray-600 font-semibold'>Total for {currentSaleItems?.length} item(s)</p>
                                                    <div className='border rounded-lg px-4 py-2 text-2xl font-bold'>{convertToCurrencyRounded(total)}</div>
                                                    {pharmacyMetadata.baseUpiId && (
                                                        <div className='mt-4 flex flex-col items-center'>
                                                            <p className='text-gray-600 text-sm mb-2'>Scan to Pay via UPI</p>
                                                            <QRCode
                                                                value={`upi://pay?pa=${pharmacyMetadata.baseUpiId}&pn=${pharmacyMetadata.pharmacyName}&am=${total}`}
                                                                size={128}
                                                            />
                                                            <p className='text-xs mt-2'>UPI ID: {pharmacyMetadata.baseUpiId}</p>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* No UPI amount input, always use total */}
                                            </div>
                                            <AttachPatient setPatientInfo={setPatientInfo} patientInfo={patientInfo} />
                                        </div>
                                    </TabsContent>
                                )}
                                <TabsContent value="split">
                                    <div className='grid grid-cols-2 divide-x-2 w-screen'>
                                        <div className='mx-auto w-1/2'>
                                            <div className='flex flex-col items-center justify-center'>
                                                <p className='text-gray-600 font-semibold'>Total</p>
                                                <div
                                                    className='border rounded-lg px-4 py-2 text-2xl font-bold'
                                                >{convertToCurrencyRounded(total)}</div>

                                                <div className='grid grid-cols-3 gap-x-4 mt-4'>
                                                    <div>
                                                        <p className='text-gray-600 font-semibold text-sm text-center'>Card Amount</p>
                                                        <div
                                                            className='border rounded-lg px-4 py-2 text-lg font-bold'
                                                        >{convertToCurrencyRounded(cardAmount!)}</div>
                                                    </div>
                                                    <div>
                                                        <p className='text-gray-600 font-semibold text-sm text-center'>UPI Amount</p>
                                                        <div
                                                            className='border rounded-lg px-4 py-2 text-lg font-bold'
                                                        >{convertToCurrencyRounded(upiAmount!)}</div>
                                                    </div>
                                                    <div>
                                                        <p className='text-gray-600 font-semibold text-sm text-center'>Total cash</p>
                                                        <div
                                                            className='border rounded-lg px-4 py-2 text-lg font-bold'
                                                        >{convertToCurrencyRounded(total - cardAmount! - upiAmount!)}</div>
                                                    </div>
                                                </div>
                                                <br></br>
                                                <div>
                                                    <p className='text-gray-600 font-semibold text-sm text-center'>{(cashReceived! + cardAmount! + upiAmount!) >= total ? "Change" : "Balance"}</p>
                                                    <div
                                                        className='border rounded-lg px-4 py-2 text-lg font-bold'
                                                    >{convertToCurrencyRounded(cashReceived! + cardAmount! + upiAmount! - total, false)}</div>
                                                </div>
                                            </div>

                                            <div className='flex flex-col max-w-fit mt-4 pt-4'>
                                                <div className='flex gap-1'>
                                                    <div>
                                                        <p className='text-sm font-semibold mt-4 mb-2'>Card Amount</p>
                                                        <Input
                                                            placeholder='45.00'
                                                            type='number'
                                                            value={cardAmount!}
                                                            onChange={e => setCardAmount(e.target.value ? parseFloat(e.target.value) : null)}
                                                        />
                                                    </div>

                                                    <div>
                                                        <p className='text-sm font-semibold mt-4 mb-2'>Approval Code</p>
                                                        <Input
                                                            placeholder='16CF'
                                                            value={approvalCode}
                                                            onChange={e => setApprovalCode(e.target.value.toUpperCase())}
                                                        />
                                                    </div>
                                                    <div>
                                                        <p className='text-sm font-semibold mt-4 mb-2'>Last 4 Digits</p>
                                                        <Input
                                                            placeholder='5678'
                                                            type='number'
                                                            value={last4digits}
                                                            onChange={e => setLast4digits(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className=''>
                                                    <p className='text-sm font-semibold mt-4 mb-2'>Cash Received<span className='text-red-400'>*</span></p>
                                                    <Input
                                                        placeholder='45.00'
                                                        type='number'
                                                        value={cashReceived!}
                                                        onChange={e => setCashReceived(e.target.value ? parseFloat(e.target.value) : null)}
                                                    />


                                                </div>
                                                <div className=''>
                                                    <p className='text-sm font-semibold mt-4 mb-2'>UPI</p>
                                                    <Input
                                                        placeholder='45.00'
                                                        type='number'
                                                        value={upiAmount!}
                                                        onChange={e => setUpiAmount(e.target.value ? parseFloat(e.target.value) : null)}
                                                    />


                                                </div>
                                            </div>
                                        </div>

                                        <AttachPatient setPatientInfo={setPatientInfo} patientInfo={patientInfo}
                                        />
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </div>
                        <SheetFooter className='w-full border-t-[0.5px] pt-4 mt-2'>
                            <div className='flex w-screen items-center justify-between'>
                                <p>{patientInfo ? patientInfo.name + " | " + patientInfo.phone + " | " + patientInfo.govt_identifier : "OTC Patient"}</p>
                                <div>
                                    <Button
                                        className='px-12 py-4'
                                        variant={'outline'}
                                        size={'lg'}
                                        onClick={() => setOpenCheckout(false)}
                                    >
                                        <BadgeXIcon className='h-4 w-4 mr-2' />
                                        Cancel
                                    </Button>

                                    <Button
                                        className='bg-blue-700 ml-4 px-12 py-4'
                                        size={'lg'}
                                        onClick={async () => {

                                            try {
                                                setLoading(true)
                                                const salesItems: FinalSalesData[] = formData(currentSaleItems!, currentSaleItemsBatchesSelected!)
                                                const saleRequestData = new CreateSaleRequest()
                                                saleRequestData.patientId = patientInfo ? patientInfo.id : ""
                                                saleRequestData.isValidInsurance = false

                                                const paymentInfo: PaymentInfo[] = []
                                                if (tabState === 'cash') {
                                                    const paymentInfoCash = new PaymentInfo()
                                                    paymentInfoCash.amount = cashReceived!
                                                    paymentInfoCash.paymentMode = PaymentMode.CASH
                                                    paymentInfo.push(paymentInfoCash)
                                                } else if (tabState === 'card') {
                                                    const paymentInfoCard = new PaymentInfo()
                                                    paymentInfoCard.amount = total
                                                    paymentInfoCard.paymentMode = PaymentMode.CREDIT_CARD
                                                    paymentInfoCard.cardLastFourDigits = last4digits
                                                    paymentInfoCard.transactionId = approvalCode
                                                    paymentInfo.push(paymentInfoCard)
                                                } else if (tabState === 'split') {
                                                    if (cardAmount !== null && cardAmount > 0) {
                                                        const paymentInfoCard = new PaymentInfo()
                                                        paymentInfoCard.amount = cardAmount!
                                                        paymentInfoCard.paymentMode = PaymentMode.CREDIT_CARD
                                                        paymentInfoCard.cardLastFourDigits = last4digits
                                                        paymentInfoCard.transactionId = approvalCode
                                                        paymentInfo.push(paymentInfoCard)
                                                    }

                                                    if (upiAmount !== null && upiAmount > 0) {
                                                        const paymentInfoUpi = new PaymentInfo()
                                                        paymentInfoUpi.amount = upiAmount!
                                                        paymentInfoUpi.paymentMode = PaymentMode.UPI
                                                        paymentInfo.push(paymentInfoUpi)
                                                    }

                                                    const paymentInfoCash = new PaymentInfo()
                                                    paymentInfoCash.amount = cashReceived!
                                                    paymentInfoCash.paymentMode = PaymentMode.CASH
                                                    paymentInfo.push(paymentInfoCash)
                                                } else if (tabState === 'upi') {
                                                    const paymentInfoUpi = new PaymentInfo()
                                                    paymentInfoUpi.amount = total
                                                    paymentInfoUpi.paymentMode = PaymentMode.UPI
                                                    paymentInfo.push(paymentInfoUpi)
                                                }

                                                const items: SaleItem[] = []
                                                for (const saleItem of salesItems) {
                                                    const item = new SaleItem()
                                                    item.batchId = saleItem.inventory_id
                                                    item.itemDiscount = getDiscount({
                                                        price: saleItem.price,
                                                        quantity: saleItem.quantity_added || 0,
                                                        discount_amt: saleItem.item_discount || 0,
                                                        discount_type: saleItem.discount_type || ""
                                                    })
                                                    item.quantity = saleItem.quantity_added || 0
                                                    item.unitSellingPrice = saleItem.price
                                                    const finalTax = saleItem.tax_profile.applicableTaxes.map(tax => {
                                                        const countryCode = JSON.parse(localStorage.getItem("pharmacyMetadata") || "{}").countryCode;
                                                        if (countryCode === "AE") {
                                                            if (tax.componentCode.includes("VAT")) {
                                                                return {
                                                                    name: tax.componentCode,
                                                                    rate: tax.ratePercent,
                                                                    amount: tax.ratePercent ? (((saleItem.price * saleItem.quantity_added!) - item.itemDiscount) * tax.ratePercent / 100) : 0
                                                                }
                                                            }
                                                        } else if (countryCode === "IN") {
                                                            if (tax.componentCode.includes("CGST") || tax.componentCode.includes("SGST")) {
                                                                return {
                                                                    name: tax.componentCode,
                                                                    rate: tax.ratePercent,
                                                                    amount: tax.ratePercent ? (((saleItem.price * saleItem.quantity_added!) - item.itemDiscount) * tax.ratePercent / 100) : 0
                                                                }
                                                            }
                                                        } else {
                                                            return {
                                                                name: tax.componentCode,
                                                                rate: tax.ratePercent,
                                                                amount: tax.ratePercent ? (((saleItem.price * saleItem.quantity_added!) - item.itemDiscount) * tax.ratePercent / 100) : 0
                                                            }
                                                        }
                                                    }).filter(tax => tax !== undefined)
                                                    const totalTax = finalTax.reduce((acc, tax) => acc + tax?.amount!, 0)
                                                    item.taxableAmount = (saleItem.price * saleItem.quantity_added!) - item.itemDiscount
                                                    item.totalTaxAmount = totalTax
                                                    item.totalAmount = ((saleItem.price * saleItem.quantity_added!) - item.itemDiscount) + totalTax
                                                    item.taxes = finalTax.map(tax => {
                                                        const taxInfo = new TaxInfo()
                                                        taxInfo.componentCode = tax?.name!
                                                        taxInfo.rate = tax?.rate!
                                                        taxInfo.amount = tax?.amount!
                                                        return taxInfo
                                                    })
                                                    item.itemType = saleItem.type === "MEDICAL" ? ItemType.MEDICAL : ItemType.NON_MEDICAL

                                                    items.push(item)
                                                }

                                                saleRequestData.items = items
                                                saleRequestData.payments = paymentInfo

                                                const response = await pharmacyClient.createSale(saleRequestData)

                                                await db.transaction('rw', db.sales_items, db.sales_items_batches, async (tx) => {
                                                    await tx.sales_items.where('sale_id').equals(internalSalesId).delete()
                                                    await tx.sales_items_batches.where('sale_id').equals(internalSalesId).delete()
                                                })
                                                await db.transaction('rw', db.active_sales, async (tx) => {
                                                    const activeNumberOfSales = await tx.active_sales.count()
                                                    if (activeNumberOfSales === 1) {
                                                        await tx.active_sales.clear()
                                                        await tx.active_sales.add({ created_at: new Date(), selected: true })
                                                    } else {
                                                        await tx.active_sales.where('id').equals(internalSalesId).delete()
                                                    }
                                                })

                                                setLoading(false)
                                                setOpenCheckout(false)

                                                navigate({
                                                    to: `/receipt/${response.saleId}`,
                                                    search: { return_to: "sales" }
                                                })
                                            } catch (error) {
                                                setLoading(false)
                                                console.error("Error during checkout:", error)
                                                toast({
                                                    title: "Checkout Failed",
                                                    description: "An error occurred while processing the sale. Please try again.",
                                                    variant: "destructive"
                                                })
                                            }
                                        }}
                                        disabled={
                                            loading ||
                                            (tabState === 'cash' && (
                                                cashReceived === null ||
                                                addDiscount === null ||
                                                currentSaleItems?.length === 0 ||
                                                (cashReceived! + addDiscount!) < total
                                            )) ||
                                            (tabState === 'card' && (
                                                approvalCode === "" ||
                                                last4digits === "" ||
                                                addDiscount === null ||
                                                currentSaleItems?.length === 0
                                            )) ||
                                            (tabState === 'upi' && (
                                                pharmacyMetadata.countryCode !== "IN" ||
                                                addDiscount === null ||
                                                currentSaleItems?.length === 0
                                                // No upiAmount check, always enabled for UPI
                                            )) ||
                                            (tabState === 'split' && (
                                                (
                                                    // Enable if either card or UPI is valid
                                                    !(
                                                        (cardAmount !== null && cardAmount > 0 && approvalCode !== "" && last4digits !== "") ||
                                                        (pharmacyMetadata.countryCode === "IN" && upiAmount !== null && upiAmount > 0)
                                                    ) ||
                                                    cashReceived === null ||
                                                    addDiscount === null ||
                                                    currentSaleItems?.length === 0 ||
                                                    ((cashReceived! + addDiscount! + (cardAmount || 0) + (pharmacyMetadata.countryCode === "IN" ? (upiAmount || 0) : 0)) < total)
                                                )
                                            ))
                                        }
                                    >
                                        {loading
                                            ?
                                            "Please Wait..."
                                            :
                                            <>
                                                <BadgeCheck className='h-4 w-4 mr-2' />
                                                Checkout
                                            </>
                                        }

                                    </Button>
                                </div>
                            </div>
                        </SheetFooter>
                    </SheetContent>
                </Sheet>
            </div>
        </div>
    )
}

export default Footer